import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { BehaviorSubject, lastValueFrom, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { EntityCredential } from '../models/credential/entityCredential';
import { BaseService } from './base.service';
import { ApiVersions } from './api-version';
import { catchError, map } from 'rxjs/operators';
import { EntityCredentialApplication } from '../models/credential/entityCredentialApplication';
import {
  PersonCredentialScenario,
  PersonCredentialScenarioStatus
} from '../models/credential/personCredentialScenario';
import { Note } from 'src/app/people/models/note';
import { Person } from 'src/app/people/models/person';
import { PersonScenarioRequirement } from '../models/credential/personScenarioRequirement';
import {
  CredentialEvidenceModel,
  IAmEvidenceDetails,
  IEvidenceSnapshotModel
} from '../models/credential/personScenarioEvidenceDetails';
import { CredentialStatus } from '../models/credential/credentialStatus';
import { PersonCredentialApplicationStatusUpdate } from '../models/credential/personCredentialApplicationStatusUpdate.model';
import { IEmailMessageWrite } from '../models/emailMessage';
import { IEntityCredentialPaymentDetails } from '../models/credential/entityCredentialPayment';
import { HistoryModel } from 'src/app/people/models/history';
import {
  PersonCredentialPatchDatesModel,
  UpdateCredentialDateType
} from '../models/credential/personCredentialPatchDates.model';
import { EntityCredentialSpecialtyEdit } from '../models/credential/entityCredentialSpecialtyEdit';
import { EntityCredentialSpecialty } from '../models/credential/entityCredentialSpecialty';
import { CredentialSpecialtyDefinitionModel } from 'src/app/admin/admin-home/admin-credentials/admin-credential-specialty/credential-specialty-definition.model';
import { CredentialDefinitionType } from '../models/credential/credential-definition.model';
import { IAddressModel } from 'src/app/people/models/addressModel';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { TeamManagersModel } from 'src/app/teams/team-managers/team-managers.model';
import { InvoiceModel } from 'src/shared-app-portal/components/profile-payments/invoice-model';
import { CredentialApplicationDefinitionType } from '../models/credential/CredentialApplicationDefinitionType';
import { CredentialApplicationFormModel } from 'src/app/people/models/credential-application-form.model';
import { ModalService } from 'src/shared-app-portal/services/modal.service';
import { PointOfContact } from 'src/app/people/models/pointOfContact';
import { AssignableScenarioModel } from 'src/app/admin/admin-home/admin-credentials/admin-credential-scenario/assignable-scenario-model';
import { getErrorMessage } from './error-handler-factory.service';
import { EntityApplicationSupportingDocument } from 'src/app/people/models/entity-application-supporting-documents.model';

/** this service is created from CredentialSingletonServiceResolver */
@Injectable({
  providedIn: 'root'
})
export class CredentialSingletonService extends BaseService {
  public credentialName: string;
  public entityName: string;

  /** Cache key for entityName; if it doesn't match getEntityId, reload entityName from the API. */
  private _entityNameForId: string = '';
  private _entityId: string;
  private _baseUsersPath: 'users' | 'teams';

  public readonly entityCredentials = new BehaviorSubject<EntityCredential[]>([]);
  public readonly entityInvoices = new BehaviorSubject<InvoiceModel[]>([]);

  public readonly entityCredentialApplications = new BehaviorSubject<EntityCredentialApplication[]>(
    null
  );
  public entityCredentialScenario: ReplaySubject<PersonCredentialScenario> =
    new ReplaySubject<PersonCredentialScenario>();
  public readonly notes = new BehaviorSubject<Note[]>([]);
  public readonly person = new BehaviorSubject<Person>(null);
  public readonly entityCredentialScenarioDeleted = new Subject<string>();
  public readonly entityCredentialApplicationStatusChanged = new Subject<{
    application: EntityCredentialApplication;
    previousStatusLabel: string | undefined;
  }>();
  public readonly pointOfContacts = new BehaviorSubject<PointOfContact[]>([]);

  public readonly credentialEvidence = new BehaviorSubject<IAmEvidenceDetails>(null);
  public readonly entityCredentialApplication = new BehaviorSubject<EntityCredentialApplication>(
    null
  );
  /** emits whenever a Document-type attribute is added/updated/deleted so listeners (e.g. supporting documents) can refresh */
  public readonly documentAttributeUpdated = new Subject<void>();

  constructor(private modalService: ModalService) {
    super();
  }
  setCredentialDetails(credentialName: string, entityName: string, entityNameForId: string) {
    this.credentialName = credentialName;
    if (entityName) {
      this.setEntityName(entityName, entityNameForId);
    }
  }
  private setEntityName = (entityName: string, entityNameForId: string) => {
    this.entityName = entityName;
    this._entityNameForId = entityNameForId;
    return this.entityName;
  };

  getAssignableScenarios = (
    entityCredentialApplicationId: string
  ): Promise<AssignableScenarioModel[]> => {
    if (!entityCredentialApplicationId) {
      throw new Error('entityCredentialApplicationId is required');
    }
    return lastValueFrom(
      this.get(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/assignable-scenarios`,
        false,
        ApiVersions.v1_0
      )
    );
  };

  async assignScenario(
    entityCredentialApplicationId: string,
    scenarioDefinitionId: string
  ): Promise<EntityCredentialApplication> {
    const updatedApplication = await lastValueFrom(
      this.post(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/scenarios/${scenarioDefinitionId}`,
        null,
        null,
        ApiVersions.v1_0
      ).pipe(map((app) => EntityCredentialApplication.parse(app)))
    );
    this.entityCredentialApplication.next(updatedApplication);
    return updatedApplication;
  }

  updateEntityCredentialApplicationPayment = (
    entityCredentialApplicationId: string,
    credentialPaymentId: string,
    checkSubmitted: CheckSubmitted
  ): Promise<boolean> =>
    lastValueFrom(
      this.put(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/payments/${credentialPaymentId}`,
        JSON.stringify(checkSubmitted),
        null,
        ApiVersions.v1_0
      )
    );

  getCredentialApplicationPayment = (
    entityCredentialApplicationId: string,
    credentialPaymentId: string
  ): Promise<IEntityCredentialPaymentDetails> =>
    lastValueFrom(
      this.get(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/payments/${credentialPaymentId}`,
        false,
        ApiVersions.v1_0
      )
    );

  getCredentialApplicationHistory = (
    entityCredentialApplicationId: string
  ): Promise<HistoryModel[]> =>
    lastValueFrom(
      this.get(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/history`,
        false,
        ApiVersions.v1_0
      )
    );

  getCredentialApplicationFormsSubmitted = (
    entityCredentialApplicationId: string
  ): Promise<CredentialApplicationFormModel[]> =>
    lastValueFrom(
      this.get(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/application-submitted`,
        false,
        ApiVersions.v1_0
      )
    );

  getCredentialApplicationSupportingDocuments = (
    entityCredentialApplicationId: string
  ): Promise<EntityApplicationSupportingDocument[]> =>
    lastValueFrom(
      this.get(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/supporting-documents`,
        true,
        ApiVersions.v1_0
      ).pipe(map((docs) => docs ?? []))
    );

  async updateCustomEntityCredentialApplicationStatus(
    entityCredentialApplicationId: string,
    updateStatusModel: PersonCredentialApplicationStatusUpdate
  ): Promise<EntityCredentialApplication> {
    const previousStatusLabel = this.getCurrentStatusLabel(entityCredentialApplicationId);
    const updatedApplication = await lastValueFrom(
      this.post(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/update-status`,
        JSON.stringify(updateStatusModel),
        null,
        ApiVersions.v1_0
      ).pipe(map((app) => EntityCredentialApplication.parse(app)))
    );
    this.entityCredentialApplication.next(updatedApplication);
    this.entityCredentialApplicationStatusChanged.next({
      application: updatedApplication,
      previousStatusLabel
    });
    return updatedApplication;
  }

  async completePersonCredentialApplication(
    entityCredentialApplicationId: string,
    updateStatusModel: PersonCredentialApplicationStatusUpdate
  ): Promise<EntityCredentialApplication> {
    const previousStatusLabel = this.getCurrentStatusLabel(entityCredentialApplicationId);
    const updatedApplication = await lastValueFrom(
      this.post(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/complete`,
        JSON.stringify(updateStatusModel),
        null,
        ApiVersions.v1_0
      ).pipe(map((app) => EntityCredentialApplication.parse(app)))
    );
    this.entityCredentialApplication.next(updatedApplication);
    this.entityCredentialApplicationStatusChanged.next({
      application: updatedApplication,
      previousStatusLabel
    });
    return updatedApplication;
  }

  private getCurrentStatusLabel(entityCredentialApplicationId: string): string | undefined {
    const current = this.entityCredentialApplication.value;
    return current?.id === entityCredentialApplicationId
      ? current.credentialCustomApplicationStatusDefinitionModel?.label
      : undefined;
  }

  deleteApplication = (entityCredentialApplicationId: string): Promise<boolean> =>
    lastValueFrom(
      this.delete(
        `${this.applicationsUrl}/${entityCredentialApplicationId}`,
        null,
        ApiVersions.v1_0,
        false
      )
    );

  getPersonCredentialApplicationDetails(
    entityCredentialApplicationId: string
  ): Promise<EntityCredentialApplication> {
    return lastValueFrom(
      this.get(
        `${this.applicationsUrl}/${entityCredentialApplicationId}`,
        false,
        ApiVersions.v1_0
      ).pipe(
        map((app) => {
          const credentialApplication = EntityCredentialApplication.parse(app);
          this.entityCredentialApplication.next(credentialApplication);
          return credentialApplication;
        })
      )
    );
  }

  payInvoiceByCheck = (invoiceType: CredentialDefinitionType, checkSubmitted: CheckSubmitted) => {
    const payInvoiceByCheckUrl =
      invoiceType == CredentialDefinitionType.Team && this.isPerson
        ? `${this.invoiceUrl}/check/team` //person is paying for a team invoice
        : `${this.invoiceUrl}/check`; //person is paying for a personal invoice or a team is paying for a team invoice

    return lastValueFrom(
      this.put(
        payInvoiceByCheckUrl, //is a person paying for a team invoice
        JSON.stringify(checkSubmitted),
        null,
        ApiVersions.unversioned
      ).pipe(
        map((json: InvoiceModel) => {
          if (checkSubmitted.entityRequirementId && checkSubmitted.entityScenarioId) {
            //paying for check from the requirement's UI
            return PersonScenarioRequirement.parse(json);
          }
          return json; //paying for check from credential payments UI OR payment UI
        })
      )
    );
  };

  generateCredentialInvoice = async (credentialFeeDefinitionId: string): Promise<boolean> => {
    const result = await lastValueFrom(
      this.post(
        `${this.invoiceUrl}/credential/${credentialFeeDefinitionId}/send`,
        null,
        null,
        ApiVersions.unversioned
      )
    );
    this.getInvoices();
    return !!result;
  };

  async getInvoices(): Promise<InvoiceModel[]> {
    this.entityInvoices.next([]);
    const invoices = await lastValueFrom(this.get(this.invoiceUrl, true, ApiVersions.unversioned));
    this.entityInvoices.next(invoices);
    return invoices;
  }

  getDocument(key: string): Promise<FileDetailsModel | null> {
    return lastValueFrom(
      this.get(`${this.s3Url}?key=${encodeURIComponent(key)}`, true, ApiVersions.v1_0, true).pipe(
        catchError((err) => {
          if (err.status === 404) {
            return of(null);
          }
          throw err;
        })
      )
    );
  }

  openEvidenceDocumentInS3 = async (
    fileName: string,
    evidence: IEvidenceSnapshotModel,
    credentialDefinitionId: string,
    isSnapshot: boolean
  ) => {
    const tenantId = this.appConfigService.tenant.id;

    const { evidenceSnapshotId, lookupValue: attributeDefinitionId } = evidence;

    const key = isSnapshot
      ? `${tenantId}/evidence-snapshot/${this.isTeam ? 'teams' : 'people'}/${
          this.getEntityId
        }/${credentialDefinitionId}/${evidenceSnapshotId}/${fileName}`
      : this.isTeam
        ? `${tenantId}/team-documents/${this.getEntityId}/${attributeDefinitionId}/${fileName}`
        : `${tenantId}/person-documents/${this.getEntityId}/${attributeDefinitionId}/${fileName}`;
    const result = await this.getDocument(key);
    if (result) {
      window.open(result.url, '_blank');
    } else {
      this.openSnackBar('Document not found', false);
    }
  };

  addEntityCredentialApplication(
    credentialDefinitionId: string,
    type: CredentialApplicationDefinitionType
  ): Promise<EntityCredentialApplication> {
    return lastValueFrom(
      this.post(
        this.applicationsUrl,
        JSON.stringify({
          credentialDefinitionId: credentialDefinitionId,
          type: type
        }),
        null,
        ApiVersions.v1_0
      )
    ).then((application) => {
      if (!application) {
        return null; //400
      }
      const newApplication = EntityCredentialApplication.parse(application);
      this.entityCredentialApplications.next([
        ...this.entityCredentialApplications.value,
        newApplication
      ]);
      if (!this.entityCredentials.value.find((x) => x.id == newApplication.entityCredentialId)) {
        //application added and credential is new. refresh all credentials
        this.getEntityCredentials();
      }
      return newApplication;
    });
  }
  updatePersonCredentialScenario(
    entityCredentialScenarioId: string,
    payload: { status: PersonCredentialScenarioStatus; note?: string }
  ): Promise<boolean> {
    return lastValueFrom(
      this.put(
        `${this.scenarioUrl}/${entityCredentialScenarioId}`,
        JSON.stringify(payload),
        null,
        ApiVersions.v1_0
      )
    ).then((json) => {
      if (!json) {
        return false;
      }
      const model = this.parseScenarioAndUpdateObservable(json);
      if (model.status !== 'Completed') {
        return true;
      }

      //scenario was completed, update the credential
      this.entityCredentials.next([
        ...this.entityCredentials.value.map((c) => {
          if (c.credentialDefinitionId === model.credentialDefinitionId) {
            c.status = CredentialStatus.Active;
          }
          return c;
        })
      ]);
      return true;
    });
  }

  async getNotes(): Promise<Note[]> {
    const notes = await lastValueFrom(this.get(this.notesUrl, false, ApiVersions.v1_0));

    this.notes.next(notes);
    return notes;
  }

  async createNote(note: Note): Promise<boolean> {
    let newNoteStream = JSON.stringify(note);
    const result = await lastValueFrom(
      this.post(this.notesUrl, newNoteStream, null, ApiVersions.v1_0)
    );
    if (result) {
      this.getNotes();
      return true;
    }
    return false;
  }

  async deleteNote(noteId: string): Promise<boolean> {
    const isSuccessful = await this.modalService.openDeleteModal('note', {
      onOkay: () => lastValueFrom(this.delete(`${this.notesUrl}/${noteId}`, null, ApiVersions.v1_0))
    });

    if (isSuccessful) {
      this.getNotes();
      this.openSnackBar(`Successfully deleted note`, true);
    }
    return isSuccessful;
  }

  async deletePersonCredentialSpecialty(
    entityCredentialApplicationId: string,
    credentialSpecialtyDefinitionId: string
  ): Promise<boolean> {
    const path = `${this.applicationsUrl}/${entityCredentialApplicationId}/specialty/${credentialSpecialtyDefinitionId}`;
    return await lastValueFrom(this.delete(path, null, ApiVersions.v1_0));
  }

  completeRequirement(
    entityScenarioId: string,
    entityRequirementId: string,
    payload: MarkScenarioComplete
  ): Promise<PersonScenarioRequirement> {
    return lastValueFrom(
      this.put(
        `${this.scenarioUrl}/${entityScenarioId}/requirements/${entityRequirementId}`,
        JSON.stringify(payload),
        null,
        ApiVersions.v1_0
      ).pipe(map((jsonResult) => PersonScenarioRequirement.parse(jsonResult)))
    );
  }

  /** gets entity's name based on getEntityId. will cache since it could be called multiple times for same id */
  getEntityName = async () => {
    if (this.entityName && this._entityNameForId === this.getEntityId) {
      return this.entityName;
    }
    const queryEntityId = this.getEntityId;

    const json = await lastValueFrom(this.get(this.entityUrl, false, ApiVersions.v1_0, true));
    if (this.isTeam) {
      const team = json as { name: string; publicName: string };
      const teamName = team?.publicName || team?.name;
      if (!teamName) {
        throw new Error('Team not found');
      }

      return this.setEntityName(teamName, queryEntityId);
    }

    const person = this.convertPerson(json);
    if (!person) {
      this.person.error({ message: 'Person not found', status: 404 });
      throw new Error('Person not found');
    }
    this.person.next(person);

    return this.setEntityName(person.fullName, queryEntityId);
  };

  private convertPerson(data): Person {
    if (!data) return null;
    return new Person(data);
  }

  getScenarioEvidences = async (
    entityScenarioId: string,
    entityRequirementId: string
  ): Promise<IAmEvidenceDetails> =>
    await lastValueFrom(
      this.get(
        `${this.scenarioUrl}/${entityScenarioId}/requirements/${entityRequirementId}/evidences`,
        false,
        ApiVersions.v1_0
      )
    ).then((result) => {
      this.credentialEvidence.next(result);
      return result;
    });

  getEntityAvailableCredentialsAutomations = (): Promise<{
    toComplete: PersonScenarioRequirement[];
    notComplete: PersonScenarioRequirement[];
  }> =>
    lastValueFrom(
      this.get(`${this.credentialsUrl}/automations`, false, ApiVersions.v1_0).pipe(
        map((jsonResult) => jsonResult)
      )
    );

  queueEntityAvailableCredentialsAutomations = () =>
    lastValueFrom(this.post(`${this.credentialsUrl}/automations`, null, null, ApiVersions.v1_0));

  deleteEntityCredentialScenario(entityCredentialScenarioId: string): Promise<boolean> {
    return lastValueFrom(
      this.delete(`${this.scenarioUrl}/${entityCredentialScenarioId}`, null, ApiVersions.v1_0).pipe(
        map(() => {
          this.entityCredentialScenario.next(null);

          this.entityCredentialScenarioDeleted.next(entityCredentialScenarioId);
          return true;
        })
      )
    );
  }

  getEntityCredentialScenario = (entityCredentialScenarioId: string) =>
    lastValueFrom(
      this.get(`${this.scenarioUrl}/${entityCredentialScenarioId}`, false, ApiVersions.v1_0)
    ).then((json) => this.parseScenarioAndUpdateObservable(json));

  private parseScenarioAndUpdateObservable(json: any) {
    const model = PersonCredentialScenario.parseWithRequirement(json);
    this.entityCredentialScenario.next(model);
    return model;
  }

  getEntityCredentials() {
    this.get(this.credentialsUrl, false, ApiVersions.v1_0)
      .pipe(map(EntityCredential.parseMany))
      .subscribe((data) => {
        if (data) {
          this.entityCredentials.next(data);
        }
      });
  }

  changeManualStatus = (
    entityCredentialId: string,
    labelId: string,
    note: string
  ): Promise<boolean> =>
    lastValueFrom(
      this.patch(
        `${this.credentialsUrl}/${entityCredentialId}/change-manual-status`,
        JSON.stringify({
          labelId: labelId,
          note: note
        }),
        ApiVersions.v0_1
      )
    );

  getEntityCredentialApplications = () =>
    lastValueFrom(this.get(this.applicationsUrl, false, ApiVersions.v1_0)).then((json) => {
      const model = EntityCredentialApplication.parseMany(json);
      this.entityCredentialApplications.next(model);
      return model;
    });

  getCredentialPdf = (entityCredentialId: string) =>
    this.get(
      `${this.credentialsUrl}/${entityCredentialId}/download-credential-pdf`,
      false,
      ApiVersions.v1_0
    );

  sendWallCertificateEmail = (entityCredentialId: string, message: IEmailMessageWrite) =>
    lastValueFrom(
      this.post(
        `${this.credentialsUrl}/${entityCredentialId}/send-credential-pdf`,
        JSON.stringify(message),
        null,
        ApiVersions.v1_0
      )
    );

  getCredentialScenarioHistory = (scenarioId: string): Promise<HistoryModel[]> =>
    lastValueFrom(this.get(`${this.scenarioUrl}/${scenarioId}/history`, false, ApiVersions.v1_0));

  updateCredentialDate(
    entityCredentialId: string,
    model: PersonCredentialPatchDatesModel
  ): Promise<boolean> {
    let body = JSON.stringify(model);

    return lastValueFrom(
      this.patch(
        `${this.credentialsUrl}/${entityCredentialId}/change-date`,
        body,
        ApiVersions.v0_1
      ).pipe(
        map((res: boolean) => {
          if (res) {
            this.openSnackBar(
              `${
                model.updateCredentialDateType == UpdateCredentialDateType.IssueDate
                  ? 'Issue date'
                  : 'Expiration date'
              } successfully updated.`,
              true
            );
            return true;
          }
          return false;
        }),
        catchError((err) => {
          this.openSnackBar(err.error, false);
          return of(false);
        })
      )
    );
  }

  init(route: ActivatedRouteSnapshot) {
    if (!route?.params) {
      console.error(`CredentialSingletonService: Route params are not available.`);
      this._entityId = null;
      this._baseUsersPath = null;
    }
    if (route.params.personId) {
      this._entityId = route.params.personId;
      this._baseUsersPath = 'users';
    } else if (route.parent?.params?.personId) {
      this._entityId = route.parent.params.personId;
      this._baseUsersPath = 'users';
    } else if (route.params.teamId) {
      this._entityId = route.params.teamId;
      this._baseUsersPath = 'teams';
    } else if (route.parent?.params?.teamId) {
      this._entityId = route.parent.params.teamId;
      this._baseUsersPath = 'teams';
    } else {
      console.error(
        `CredentialSingletonService: Unable to initialize. Route params do not contain personId or teamId.`
      );
      this._baseUsersPath = null;
      this._entityId = null;
    }
  }

  initForPerson(personId: string) {
    this._entityId = personId;
    this._baseUsersPath = 'users';
  }

  initForTeam(teamId: string) {
    this._entityId = teamId;
    this._baseUsersPath = 'teams';
  }

  get isPerson(): boolean {
    return this._baseUsersPath === 'users';
  }

  get isTeam(): boolean {
    return this._baseUsersPath === 'teams';
  }

  public getLinkToCredentialPage = () => {
    return [this.baseLink(), this.getEntityId, 'credentials'];
  };

  private baseLink = () => {
    if (this.isPerson) {
      return '/people/profile';
    }

    if (this.isTeam) {
      return '/teams/beta';
    }
    throw new Error(
      'isPerson and isTeam not set. Please make sure CredentialSingletonServiceResolver is used in the route.'
    );
  };

  public getLinkToApplicationPage = (applicationId: string): string[] => {
    return [this.baseLink(), this.getEntityId, 'credentials', 'applications', applicationId];
  };

  get getEntityId(): string {
    if (!this._entityId) {
      throw new Error(
        'Entity ID is not initialized. Please make sure CredentialSingletonServiceResolver is used in the route.'
      );
    }
    return this._entityId;
  }

  async updateScenarioTags(entityCredentialScenario: PersonCredentialScenario) {
    return this.put(
      `${this.scenarioUrl}/${entityCredentialScenario.id}/tags`,
      JSON.stringify(entityCredentialScenario.tags),
      null,
      ApiVersions.v1_0
    ).subscribe(() => {
      this.getEntityCredentialScenario(entityCredentialScenario.id);
    });
  }

  async getSpecialtyEvidence(
    entityCredentialApplicationId: string
  ): Promise<CredentialEvidenceModel> {
    const path = `${this.applicationsUrl}/${entityCredentialApplicationId}/specialty/evidence`;

    return await lastValueFrom(this.get(path, null, ApiVersions.v1_0)).then((result) => {
      this.credentialEvidence.next(result);
      return result;
    });
  }

  getAvailableSpecialties(
    entityCredentialApplicationId: string
  ): Promise<CredentialSpecialtyDefinitionModel[]> {
    return lastValueFrom(
      this.get(
        `${this.applicationsUrl}/${entityCredentialApplicationId}/specialty/available`,
        null,
        ApiVersions.v1_0
      )
    );
  }

  editPersonCredentialSpecialty(
    entityCredentialApplicationId: string,
    model: EntityCredentialSpecialtyEdit
  ): Promise<EntityCredentialSpecialty> {
    const path = `${this.applicationsUrl}/${entityCredentialApplicationId}/specialty`;
    return lastValueFrom(this.put(path, JSON.stringify(model), null, ApiVersions.v1_0));
  }

  /** if addressValidationCacheKey set, will validate the address
   * @param {string} addressValidationCacheKey if set will validate address */
  public saveAddress = (
    address: IAddressModel,
    addressValidationCacheKey: string
  ): Promise<DemographicInfo> =>
    lastValueFrom(
      this.post(
        this.demographicInfoUrl,
        { ...address, addressValidationCacheKey: addressValidationCacheKey },
        null
      )
    ).then((json) => new DemographicInfo(json));

  getPointOfContacts = (): Observable<PointOfContact[]> => {
    if (this.pointOfContacts.value.length) {
      this.pointOfContacts.next([]);
    }

    lastValueFrom(this.get<PointOfContact[]>(this.PointOfContactsUrl, true, ApiVersions.v0_1, true))
      .then((data) => {
        this.pointOfContacts.next(data.map((contact) => PointOfContact.parse(contact)));
      })
      .catch((e) => {
        this.openSnackBar(getErrorMessage(e, 'Failed to load point of contacts'), false);
      });
    return this.pointOfContacts;
  };

  async addPointOfContact(pointOfContact: { personContactId: string }) {
    const body = JSON.stringify({ personContactId: pointOfContact.personContactId });

    const result = await lastValueFrom(this.post(this.PointOfContactsUrl, body, null));
    if (result) {
      const newManager = {
        ...PointOfContact.parse(result),
        id: result.id,
        imageId: result.imageId,
        name: result.name,
        email: result.email
      };

      this.pointOfContacts.next([...this.pointOfContacts.value, newManager]);
      this.openSnackBar('Point of contact added successfully', true);
    }
    return result;
  }

  async removePointOfContact(id: string) {
    lastValueFrom(this.delete(`${this.PointOfContactsUrl}/${id}`))
      .then(() => {
        this.pointOfContacts.next(this.pointOfContacts.value.filter((pc) => pc.id !== id));
        this.openSnackBar('Point of contact removed successfully', true);
      })
      .catch(() => {
        this.openSnackBar('Failed to remove point of contact', false);
      });
  }

  get credentialsUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/credentials`;
  }

  get applicationsUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/applications`;
  }
  get scenarioUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/scenarios`;
  }
  get notesUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/notes`;
  }
  get entityUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}`;
  }
  get s3Url(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/documents/signed-url`;
  }
  get invoiceUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/invoices`;
  }
  get demographicInfoUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/demographicInfo/address`;
  }

  get PointOfContactsUrl(): string {
    return `${this._baseUsersPath}/${this.getEntityId}/point-of-contacts`;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CredentialSingletonServiceResolver {
  constructor(private credentialService: CredentialSingletonService) {}
  resolve(route: ActivatedRouteSnapshot) {
    this.credentialService.init(route);
    return this.credentialService;
  }
}

type MarkScenarioComplete = {
  isVerified?: boolean;
  note?: string;
};

type FileDetailsModel = {
  key: string;
  url: string;
};

type CheckSubmitted = {
  nameOnCheck: string;
  checkNumber: string;
  comment: string;
  invoiceId: string;
  entityRequirementId?: string;
  entityScenarioId?: string;
};
